'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import { useState, useContext, useEffect } from 'react'
import {
  FieldValidator,
  useField as useFormikField,
  useFormikContext
} from 'formik'

// walrus
import { useComponentStates } from '../../_support'

// lib
import { ENV } from '@lib/env'
import { validateRequired } from '../validators'
import { ThemeVariantContext } from '../../_support'
import { distribute } from '../../_support'
import FieldContainer from './field-container'

// styles - patches autofill bugs
import './field-styles'

/* -----------------------------------------------------------------------------
 * useField
 * -------------------------------------------------------------------------- */

export type FieldState = 'focused' | 'filled' | 'empty'

export type UseFieldOptions = {
  format?: (text: string) => string
  last?: boolean
  name: string
  parse?: (text: string) => string
  required?: boolean | string
  validate?: FieldValidator
}

const useField = ({
  format = text => text,
  last,
  name,
  parse = text => text,
  required,
  validate,
  ...rest
}: UseFieldOptions) => {
  const themeVariant = useContext(ThemeVariantContext)
  const [fieldAutofilled, setFieldAutofilled] = useState<boolean>(false)
  const [showFieldError, setShowFieldError] = useState<boolean>(false)
  const formik = useFormikContext()
  const [field, fieldMeta] = useFormikField({
    name,
    validate: value => {
      const requiredMsg = typeof required === 'string' ? required : undefined
      const requiredError = required && validateRequired(requiredMsg)(value)
      const validationError = validate && validate(value)

      return requiredError || validationError
    }
  })

  // Allow submitting form when "enter" key press on last field
  // Note: Casting handler to any due to implementation of formik typings:
  // https://github.com/jaredpalmer/formik/issues/376
  const fieldAdditional =
    last && !formik.isSubmitting
      ? { onSubmitEditing: formik.handleSubmit as any }
      : {}

  // Hack to determine if field is autofilled
  // ref: https://codedaily.io/tutorials/69/Animated-Input-Label-with-Chrome-Autofill-Detection-in-React
  if (ENV['client_type'] === 'web') {
    Object.assign(fieldAdditional, {
      onAnimationStart: e =>
        setFieldAutofilled(e.animationName === 'onAutoFillStart')
    })
  }

  // Modify formik handlers to work with react-native-web
  const { onChange, onBlur, value, ...fieldRest } = field
  const fieldProps = {
    ...fieldRest,
    ...fieldAdditional,
    onChangeText: text => {
      // Note: Disables field error after the user starts to type again
      setShowFieldError(false)
      if (formik.status) {
        formik.setStatus(undefined)
      }
      onChange(name)(parse(text))
    },
    onBlur: onBlur(name),
    value: format(value)
  }

  const [containerProps, inputProps] = distribute(rest, {
    using: FieldContainer.styleKeys,
    to: {},
    rest: { ...fieldProps }
  })

  const [listeners, states, inputRest] = useComponentStates(inputProps, [
    'focus'
  ])

  const fieldState: FieldState = states.focus(true)
    ? 'focused'
    : inputProps.value !== '' || fieldAutofilled
    ? 'filled'
    : 'empty'

  // Note: Ensuring we will only show the field error message when
  // the form is submitted
  useEffect(() => {
    setShowFieldError(true)
  }, [formik.submitCount])

  const fieldError =
    fieldMeta.touched &&
    fieldMeta.error &&
    !formik.isSubmitting &&
    formik.submitCount > 0 &&
    showFieldError
      ? fieldMeta.error
      : undefined

  const inputStyles = {
    base: {
      shared: {
        p: '12',
        pt: 30 as any,
        font: 'text.15.medium',
        borderWidth: 1
      },
      default: {
        textColor: 'purple',
        borderColor: 'paleBlue',
        bg: 'paleBlue'
      },
      punchThroughGray: {
        textColor: 'shade06',
        borderColor: 'shade03',
        bg: 'white'
      }
    },
    focused: {
      default: {
        bg: 'white',
        borderColor: 'violet'
      },
      punchThroughGray: {
        borderColor: 'shade06',
        bg: 'white'
      }
    },
    filled: {
      default: {
        bg: 'white',
        borderColor: 'shade04'
      },
      punchThroughGray: {
        bg: 'white',
        borderColor: 'shade04'
      }
    },
    error: {
      sr: '24',
      bg: 'white',
      borderColor: fieldState === 'focused' ? 'violet' : 'error'
    },
    empty: {}
  } as const

  const inputStyleProps = {
    ...inputStyles['base']['shared'],
    ...inputStyles['base'][themeVariant || 'default'],
    ...inputStyles[fieldState][themeVariant || 'default'],
    ...(fieldError ? inputStyles.error : undefined),
    ...(fieldAutofilled ? { bg: '#f4f8fe' as any } : undefined)
  }

  return {
    formik,
    fieldMeta,
    fieldState,
    fieldError,
    fieldAutofilled,
    containerProps,
    inputProps: {
      ...listeners,
      ...inputStyleProps,
      ...inputRest
    }
  } as const
}

export default useField
